import { useParams, Link } from 'react-router-dom';
import SEOMeta from '@/components/base/SEOMeta';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { categories, allCategories } from '@/mocks/categories';
import { getComicsByCategory, featuredComics } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug) || allCategories.find((c) => c.slug === slug);

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-heading font-black text-foreground-50">分类未找到</h1>
          <Link to="/" className="mt-4 inline-block text-primary-400 hover:text-primary-300">返回首页</Link>
        </div>
      </div>
    );
  }

  const categoryComics = getComicsByCategory(category.slug);
  const featuredComic = categoryComics[0];
  const otherComics = categoryComics.slice(1);
  const seoTitle = `${category.name}韩国漫画 - 免费${category.name}韩漫在线阅读`;

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'http://ynbskj.cn/' },
      { '@type': 'ListItem', position: 2, name: category.name, item: `http://ynbskj.cn/category/${category.slug}` },
    ],
  };

  return (
    <>
      <SEOMeta
        title={seoTitle}
        description={`韩漫网${category.name}分类提供${category.comicCount}部免费${category.name}韩国漫画在线免费阅读，${category.description}。每日更新，高质量画质，支持多设备阅读。`}
        keywords={`${category.name}漫画,${category.name}韩漫,韩国${category.name}漫画,免费${category.name}漫画,${category.name}漫画在线阅读`}
        canonicalUrl={`http://ynbskj.cn/category/${category.slug}`}
        ogImage="https://readdy.ai/api/search-image?query=Korean%20manhwa%20genre%20collage%2C%20dark%20atmospheric%20manga%20style%2C%20dramatic%20lighting%2C%20Korean%20webtoon%20aesthetic&width=1200&height=630&seq=category-og-default&orientation=landscape"
        jsonLd={[breadcrumbLd]}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 pb-14">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', to: '/' },
                  { label: category.name },
                ]}
              />
            </div>

            <div className="mb-12">
              <span className="inline-block px-3 py-1 text-[11px] font-label font-medium text-foreground-500 border border-background-300/60 rounded-full whitespace-nowrap">
                {category.nameEn} · {category.comicCount.toLocaleString()} 部作品
              </span>
              <h1 className="mt-4 text-2xl md:text-4xl font-heading font-black text-foreground-50">
                {category.name}
                <span className="text-accent-400"> 漫画</span>
              </h1>
              <p className="mt-2 text-sm text-foreground-500 max-w-lg">{category.description}</p>
            </div>

            {categoryComics.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center">
                  <i className="ri-emotion-sad-line text-4xl text-foreground-600"></i>
                </div>
                <p className="mt-4 text-foreground-500">该分类暂无漫画</p>
              </div>
            ) : (
              <>
                {featuredComic && (
                  <div className="mb-12">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                      <div className="lg:col-span-1">
                        <ComicCard comic={featuredComic} featured />
                      </div>
                      <div className="lg:col-span-2 grid grid-cols-2 md:grid-cols-2 gap-4">
                        {otherComics.slice(0, 4).map((comic) => (
                          <ComicCard key={comic.id} comic={comic} />
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {otherComics.length > 4 && (
                  <div>
                    <h2 className="text-xl font-heading font-bold text-foreground-50 mb-6">
                      更多{category.name}漫画
                    </h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                      {otherComics.slice(4).map((comic) => (
                        <ComicCard key={comic.id} comic={comic} />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            <div className="mt-16 p-6 md:p-8 bg-background-100 rounded-lg">
              <h2 className="text-lg font-heading font-bold text-foreground-50 mb-4">
                关于{category.name}韩国漫画
              </h2>
              <p className="text-sm text-foreground-500 leading-relaxed">
                韩漫网{category.name}分类汇聚了最优质的{category.name}题材韩国漫画，{category.description}。
                无论你是喜欢紧张刺激的战斗场面，还是感人至深的情感故事，都能在这里找到心仪的作品。
                所有漫画均免费在线阅读，无需注册，画质高清，更新及时。
              </p>
            </div>

            <div className="mt-10 flex flex-wrap gap-2">
              <span className="text-xs text-foreground-600 font-medium whitespace-nowrap">热门分类：</span>
              {allCategories.filter((c) => c.slug !== slug).slice(0, 8).map((c) => (
                <Link
                  key={c.slug}
                  to={`/category/${c.slug}`}
                  className="px-3 py-1 text-xs rounded-full bg-background-200/40 text-foreground-400 hover:text-primary-400 hover:bg-background-200/60 transition-colors whitespace-nowrap"
                >
                  {c.name}
                </Link>
              ))}
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}